import { useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Copy, Volume2, RefreshCw, Square } from "lucide-react";
import { toast } from "sonner";
import { speak, stopSpeaking } from "@/lib/audio";
import { ArrowUpRight } from "lucide-react";
import { PROJECTS, COL } from "@/components/GroupProjects";
import { WeatherCard } from "./WeatherCard";

const HOSTS = { school: "visualstudiofacile.com", phone: "aurora-phone.online", gwd: "graficawebdesign.com" };

const ProjectCards = ({ text }) => {
  const found = PROJECTS.filter((p) => text.toLowerCase().includes(HOSTS[p.id]));
  if (!found.length) return null;
  return (
    <div className="grid sm:grid-cols-2 gap-2 mt-3" data-testid="chat-project-cards">
      {found.map(({ id, url, Icon, name, tag, d, c }) => (
        <a key={id} href={url} target="_blank" rel="noopener noreferrer" data-testid={`chat-project-card-${id}`}
          className="group flex gap-3 rounded-xl border border-cyan-500/25 bg-[#0a1224]/85 p-3 hover:border-amber-300/60 hover:-translate-y-0.5 transition-[transform,border-color] duration-300">
          <span className={`w-10 h-10 shrink-0 rounded-lg flex items-center justify-center border bg-white/5 ${COL[c]}`}><Icon size={18} /></span>
          <span className="min-w-0 flex-1">
            <span className={`block text-[10px] font-mono uppercase tracking-widest ${COL[c].split(" ")[0]}`}>{tag}</span>
            <span className="block text-sm font-semibold text-white">{name}</span>
            <span className="block text-xs text-slate-400 line-clamp-2">{d}</span>
          </span>
          <ArrowUpRight size={16} className="text-slate-400 group-hover:text-amber-300 shrink-0" />
        </a>
      ))}
    </div>
  );
};

// Turns [n] citation markers into links to the matching source (or drops them).
const linkCitations = (text) => {
  const src = {};
  (text.match(/\n\nFonti: (.*)$/s)?.[1] || "").replace(/\[(\d+)\]\(([^)]+)\)/g, (_, n, u) => { src[n] = u; return ""; });
  return text.replace(/\[(\d+)\](?!\()/g, (_, n) => (src[n] ? ` [${n}](${src[n]})` : ""));
};

const FADE = { animationDuration: ".35s" };
const MODEL_NAMES = { aurora: "Aurora", gpt: "GPT-5.4", claude: "Claude", gemini: "Gemini", grok: "Grok", kimi: "Kimi", deepseek: "DeepSeek", perplexity: "Perplexity" };

const Actions = ({ m, isLast, onRegenerate, voice, busy }) => {
  const [playing, setPlaying] = useState(false);
  const listen = async () => {
    if (playing) { stopSpeaking(); setPlaying(false); return; }
    setPlaying(true);
    try { await speak(m.content, voice); } catch (_) { toast.error("Riproduzione audio non riuscita"); }
    setPlaying(false);
  };
  const btn = "p-1.5 rounded-lg text-slate-500 hover:text-cyan-300 hover:bg-cyan-500/10 transition-colors";
  return (
    <div className="flex items-center gap-1 mt-1.5">
      <button className={btn} title="Copia" data-testid={`msg-copy-${m.id}`} onClick={() => { navigator.clipboard.writeText(m.content); toast.success("Copiato"); }}><Copy size={14} /></button>
      <button className={btn} title="Ascolta" data-testid={`msg-listen-${m.id}`} onClick={listen}>{playing ? <Square size={14} /> : <Volume2 size={14} />}</button>
      {isLast && !busy && <button className={btn} title="Rigenera" data-testid="msg-regenerate-btn" onClick={onRegenerate}><RefreshCw size={14} /></button>}
      <span className="ml-1 text-[10px] font-mono uppercase tracking-widest text-slate-600">{MODEL_NAMES[m.model] || m.model}</span>
    </div>
  );
};

export const MessageList = ({ messages, busy, onRegenerate, voice }) => {
  const lastAssistant = [...messages].reverse().find((m) => m.role === "assistant");
  return (
    <div className="space-y-5" data-testid="chat-messages">
      {messages.map((m) => m.role === "user" ? (
        <div key={m.id} className="flex justify-end fade-up" style={FADE}>
          <div data-testid="chat-msg-user" className="max-w-[85%] rounded-2xl rounded-br-md px-4 py-2.5 bg-gradient-to-br from-cyan-500/20 to-violet-500/20 border border-cyan-400/25 text-slate-100 text-sm whitespace-pre-wrap">{m.content}</div>
        </div>
      ) : (
        <div key={m.id} className="flex gap-3 fade-up" style={FADE}>
          <img src="/icon-64.png" alt="" className="w-7 h-7 rounded-lg mt-1 shrink-0 shadow-[0_0_12px_rgba(45,212,191,.4)]" />
          <div className="min-w-0 flex-1">
            {m.weather && <WeatherCard w={m.weather} />}
            <div data-testid="chat-msg-assistant" className={`md ${m.streaming ? "caret" : ""}`}>
              {m.content ? <ReactMarkdown remarkPlugins={[remarkGfm]}>{linkCitations(m.content)}</ReactMarkdown> : <span className="hud-tag !text-violet-300">Elaborazione…</span>}
            </div>
            {!m.streaming && m.content && <ProjectCards text={m.content} />}
            {!m.streaming && m.content && <Actions m={m} isLast={m.id === lastAssistant?.id} onRegenerate={onRegenerate} voice={voice} busy={busy} />}
          </div>
        </div>
      ))}
    </div>
  );
};
